import type { Guide } from './types.js';

/**
 * The task-shaped companion to USPS_GUIDE, written so a Postal Service requestor,
 * COR, or Contracting Officer can follow it from activating an account through to
 * pulling the reporting the agreement requires. It is also handed to USPS as a Word
 * document, so it describes only what a customer account can see and do.
 */
export const USPS_STEP_GUIDE: Guide = {
  title: 'USPS step-by-step guide',
  subtitle:
    'Activate your account, request a repair, follow it, authorize the price, and pull your reporting — in the order you will need to do them.',
  audience:
    'Written for USPS requestors, Contracting Officer’s Representatives, and Contracting Officers using the VGS Work Order Management portal.',
  sections: [
    {
      id: 'activate-account',
      title: '1. Activate your account and sign in',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Accounts are created by invitation only, and nobody — not VGS, not your administrator — ever sets your password. You set it yourself, from a link that works once.',
        },
        {
          kind: 'procedure',
          steps: [
            {
              do: 'Open the invitation email and follow its link.',
              where: 'The email is titled “You have been invited to VGS Work Order Management”.',
              then: 'The activation page opens and shows the email address, organization, and role you were invited as. If any of that is wrong, stop and tell the person who invited you rather than continuing.',
            },
            {
              do: 'Complete your details.',
              where: 'Full name, Job title, and Phone.',
              then: 'Your job title is recorded alongside anything you approve, so enter it as the agreement would name it — Contracting Officer, for example. The phone number is how VGS reaches you about a repair in progress.',
            },
            {
              do: 'Choose a password.',
              where: 'Choose a password and Confirm password.',
              then: 'It must be at least the stated length and mix upper and lower case letters, a digit, and a symbol.',
            },
            {
              do: 'Activate.',
              where: 'Activate account.',
              then: 'You are signed in and taken to your dashboard. The invitation link is now spent and cannot be reused.',
            },
            {
              do: 'Sign in from then on.',
              where: 'The sign-in page, with your email address and password.',
              then: 'You land on your dashboard each time.',
            },
          ],
        },
        {
          kind: 'bullets',
          items: [
            'If the invitation has expired, ask your administrator to re-invite you; a fresh single-use link is issued and the old one stops working.',
            'If you have forgotten your password, use “Forgot your password?” on the sign-in page. A reset link is emailed to you.',
            'To change a password you still know, use Change password from the account menu. Changing it signs out your other sessions.',
            'Access is granted per facility. You see the work orders, reports, and decisions for the facilities you were given and nothing else — if a facility you are responsible for is missing, that is an access question for your administrator.',
          ],
        },
      ],
    },
    {
      id: 'find-your-way-around',
      title: '2. Find your way around',
      blocks: [
        {
          kind: 'paragraph',
          text: 'The portal has four places you will use. Which of them you see depends on the role you were invited as.',
        },
        {
          kind: 'definitions',
          items: [
            {
              term: 'Dashboard',
              description:
                'Your open work orders, anything awaiting your approval, anything past its requested date, and what closed in the last thirty days. Each tile is a link into the matching list.',
            },
            {
              term: 'Work orders',
              description:
                'Every request within your facility scope. Search by number, title, equipment, or location, and filter by status, priority, facility, or a view such as open only, past requested date, or submitted by me.',
            },
            {
              term: 'Awaiting your approval',
              description:
                'Shown to CORs and Contracting Officers. The proposals VGS has submitted for a decision that is yours to make, with the total and the authority the amount requires.',
            },
            {
              term: 'Reports',
              description:
                'Work order activity and approval decisions within your scope, over any date range, exportable as a file that opens in Excel.',
            },
          ],
        },
        {
          kind: 'table',
          caption: 'What each role can do',
          headers: ['Role', 'What it can do'],
          rows: [
            [
              'USPS Requestor',
              'Report repairs to VGS, follow the resulting work orders, comment, and attach photographs. No approval authority.',
            ],
            [
              'Contracting Officer’s Representative (COR)',
              'Everything a requestor can do, plus authorize proposals up to the COR limit in the agreement, and pass one above that limit to the Contracting Officer.',
            ],
            [
              'Contracting Officer (CO)',
              'Everything a COR can do, plus authorize any amount, rule on requests to proceed with fewer than the required number of quotes, and exercise discretion where the Facilities Response Line has declined a repair.',
            ],
            [
              'Read-only',
              'Reads work orders and reports, including oversight and FSCMC access. Changes nothing.',
            ],
          ],
        },
        {
          kind: 'table',
          caption: 'What each status means in practice',
          headers: ['Status', 'What is happening', 'Whose move it is'],
          rows: [
            ['Submitted', 'Your request has been received and VGS has been notified.', 'VGS'],
            ['Under VGS Review', 'VGS has acknowledged it and is reviewing the request.', 'VGS'],
            ['Estimate in Progress', 'VGS is inspecting or scoping the repair to price it.', 'VGS'],
            [
              'Awaiting USPS Approval',
              'A proposal has been submitted and is waiting on a decision.',
              'You',
            ],
            ['Approved', 'The price is authorized and VGS may schedule the work.', 'VGS'],
            [
              'Revision Requested',
              'You asked for a change; VGS is preparing a revised proposal.',
              'VGS',
            ],
            [
              'Rejected',
              'You declined the proposal. Nothing will be performed at that price.',
              'VGS',
            ],
            ['Repair Scheduled', 'A date has been set; you can see it on the work order.', 'VGS'],
            ['Work in Progress', 'The repair is being performed.', 'VGS'],
            [
              'On Hold',
              'Something has paused the work. The reason is shown on the work order.',
              'Varies',
            ],
            ['Completed', 'The repair is finished and documented with photographs.', 'VGS'],
            ['Closed', 'The record is final and stops changing.', 'Nobody'],
          ],
        },
      ],
    },
    {
      id: 'raise-a-work-order',
      title: '3. Request a repair',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Work orders are entered by VGS, not through the portal. To request a repair, report the problem to VGS by telephone or email; VGS records it as a work order the same day and you follow it from here. Requesting a repair commits nothing and costs nothing: it asks VGS to look at the problem and come back with a price you then decide on.',
        },
        {
          kind: 'procedure',
          steps: [
            {
              do: 'Tell VGS what is wrong and where.',
              where: 'Your VGS contact for the facility, by telephone or email.',
              then: 'Give the facility, where the problem is (building or area, specific location, equipment or asset tag), what is wrong and since when, its effect on operations, and how urgent it is — Emergency is for conditions that are unsafe or that stop operations.',
            },
            {
              do: 'Name the point of contact.',
              where: 'The person VGS should call about access and scheduling.',
              then: 'This can be you or somebody at the facility. Their name, email and phone are recorded on the work order and reported as the local facility contact.',
            },
            {
              do: 'Look for the work order.',
              where: 'Work orders, on the dashboard or in the work order list.',
              then: 'VGS enters the request and a work order number is issued immediately — RE-TUL-000012, for example. The request lands in Submitted and you can open it, comment on it and attach photographs from that moment.',
            },
            {
              do: 'Attach photographs.',
              where: 'Documents & comments on the work order.',
              then: 'Photographs are the fastest way to shorten the estimate. PDF, JPG, PNG, HEIC, WEBP, DOC, DOCX, XLS, XLSX, and CSV are accepted, up to 25 MB each.',
            },
          ],
        },
      ],
    },
    {
      id: 'follow-a-work-order',
      title: '4. Follow a work order',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Open any work order from the list. The heading carries its number, status, priority, and facility, and the record is organized into tabs.',
        },
        {
          kind: 'definitions',
          items: [
            {
              term: 'Overview',
              description:
                'The problem as described, the request details, the point of contact, the invoice number once VGS records it, and the key dates — submitted, approved, completed, closed.',
            },
            {
              term: 'Investigation',
              description:
                'What VGS found when they inspected: findings, root cause, and recommended action.',
            },
            {
              term: 'Proposal',
              description:
                'The scope of work, the price you are asked to authorize, any supporting quotes, and the permanent record of every decision taken on it.',
            },
            {
              term: 'Schedule & completion',
              description:
                'The scheduled date and time window, when work actually started and finished, what was performed, and any follow-up VGS recommends.',
            },
            {
              term: 'Documents & comments',
              description:
                'Photographs and documents on the repair, and the conversation with VGS. A comment is part of the permanent record.',
            },
            {
              term: 'History',
              description:
                'Every status change, approval, comment, and document in order, with who did it and when. Nothing here is edited or overwritten.',
            },
          ],
        },
        {
          kind: 'paragraph',
          text: 'You are emailed when something needs you or when the position changes: a proposal is waiting on your decision, a repair is scheduled, work is complete. You do not need to watch the portal to stay current.',
        },
        {
          kind: 'table',
          caption: 'When it looks stuck',
          headers: ['What you see', 'What it means', 'What to do'],
          rows: [
            [
              'Sitting in Submitted or Under VGS Review',
              'VGS has it and has not yet come back with a price.',
              'Add a comment on the work order — it reaches the VGS personnel on that facility and stays in the record. Raise the priority with your COR if it is urgent.',
            ],
            [
              'Awaiting USPS Approval',
              'It is waiting on a Postal Service decision, not on VGS.',
              'Check Awaiting your approval. If the amount is above your limit, it is with the Contracting Officer.',
            ],
            [
              'Approved but not scheduled',
              'VGS has authorization and has not yet set a date.',
              'Comment on the work order asking for the schedule.',
            ],
            [
              'On Hold',
              'Something has paused the repair; the reason is in the banner at the top of the work order.',
              'Read the reason. If it is an access or facility issue, resolving it at your end is what releases the work.',
            ],
            [
              'A proposal above $10,000 that cannot be approved',
              'The Facilities Response Line determination has not been recorded on the work order yet.',
              'Call the Facilities Response Line at 855-444-6375 and record the ticket and determination on the Proposal tab.',
            ],
          ],
        },
      ],
    },
    {
      id: 'decide-on-a-proposal',
      title: '5. Decide on a proposal',
      blocks: [
        {
          kind: 'paragraph',
          text: 'No repair is performed until a Postal Service authority authorizes the price. No VGS account holds approval authority at all, so this decision is always yours.',
        },
        {
          kind: 'procedure',
          steps: [
            {
              do: 'Open what is waiting on you.',
              where:
                'Awaiting your approval in the navigation, or the tile of the same name on your dashboard.',
              then: 'Each entry shows the work order, facility, the total, and the authority the amount requires.',
            },
            {
              do: 'Read the proposal.',
              where: 'The work order → Proposal tab.',
              then: 'You see the scope of work, the findings summary, the priced lines, any supporting quotes, and the total. That total is the price being authorized; there is nothing further to add to it.',
            },
            {
              do: 'Satisfy the pre-authorization requirements, if any are shown.',
              where: 'The Proposal tab, above the proposal itself.',
              then: 'Above $10,000 a Facilities Response Line ticket and determination must be recorded, and the required number of quotes must be on file or a Contracting Officer must approve proceeding with fewer. The decision cannot be recorded until they are satisfied.',
            },
            {
              do: 'Choose your decision.',
              where: 'The Decision options: Approve, Request a revision, or Reject.',
              then: 'A comment is required for a revision or a rejection, and it is what VGS acts on. A comment on an approval is optional and recorded.',
            },
            {
              do: 'Confirm.',
              where: 'Record decision, then the confirmation dialog.',
              then: 'The dialog restates the amount because approving authorizes VGS to perform the work at that price. The approval is permanent and cannot be edited afterwards.',
            },
          ],
        },
        {
          kind: 'table',
          caption: 'What each decision does',
          headers: ['Decision', 'Effect'],
          rows: [
            [
              'Approve',
              'The work order moves to Approved, VGS is notified, and they may schedule and perform the work at that price. The approval, your name, your authority, and the amount are recorded permanently.',
            ],
            [
              'Request a revision',
              'The work order returns to VGS with your comment. They prepare a revised proposal as a new version, which comes back for a fresh decision. The original version stays in the record.',
            ],
            [
              'Reject',
              'Nothing is performed at that price. VGS may prepare a revision, or the work order is cancelled if the repair is not going ahead.',
            ],
          ],
        },
        {
          kind: 'note',
          variant: 'info',
          title: 'Authority follows the amount',
          text: 'A proposal of $5,000 or less may be authorized by a COR or by the Contracting Officer. Above $5,000 it takes two steps: the COR reviews and endorses the proposal first, and only then is the Contracting Officer asked for the final approval. VGS is notified to proceed only after the Contracting Officer approves. The portal routes each step rather than relying on anyone to remember the threshold, and it re-checks the limit and the order of the two steps when each decision is recorded.',
        },
        {
          kind: 'bullets',
          items: [
            'Above $10,000 a repair must be referred to the Facilities Response Line on 855-444-6375 before it can be authorized, and the ticket, the time of the call, and the determination are recorded on the work order.',
            'If the Response Line determines the work qualifies as a project, it leaves this process entirely and the work order is cancelled once you confirm how the project will proceed.',
            'If the Response Line declines the work, a Contracting Officer may still authorize it where doing so serves the best interest of the Postal Service, stating why on the work order.',
            'Where the required number of quotes could not be obtained, VGS must request an exception with a justification, and only a Contracting Officer can grant it.',
            'A scope that grows after approval does not change the authorized figure. It comes back as a revised proposal, and if the new total crosses a threshold it is routed to the authority that may approve the higher amount.',
          ],
        },
      ],
    },
    {
      id: 'run-your-reports',
      title: '6. Run your reports',
      blocks: [
        {
          kind: 'paragraph',
          text: 'The portal holds live data, so a report is produced whenever you want one — monthly, quarterly, annually, or on any other day. There is no request to make and nothing to wait for.',
        },
        {
          kind: 'procedure',
          steps: [
            {
              do: 'Open the reports screen.',
              where: 'Reports in the navigation.',
              then: 'The Work orders tab reports activity and cycle times; the Approvals tab reports every decision, which is the backup for an invoice.',
            },
            {
              do: 'Set the period.',
              where:
                'Submitted from and Submitted to on the Work orders tab; Decided from and Decided to on the Approvals tab.',
              then: 'A calendar month, a quarter, a fiscal year, or an arbitrary range — the cadence is simply the dates you enter. Clear resets them.',
            },
            {
              do: 'Narrow to a facility.',
              where: 'Facility.',
              then: 'Only that facility is reported. Left as all facilities, you get everything in your scope.',
            },
            {
              do: 'Choose the fields you want.',
              where: 'Choose columns, then tick the fields; Reset returns to every column.',
              then: 'The report carries the Postal Service fields — work order number, commodity or service, service location name and address, Property ID, Area/Logistics, District/Division, work description, local facility contact, requestor contact, date received, date approved, project cost, performance start date, status, invoice number, notes, and cycle times. Your choice applies to both the table and the file you export.',
            },
            {
              do: 'Export it.',
              where: 'Export CSV, top right.',
              then: 'A CSV downloads with exactly the columns you chose. It opens directly in Excel and its numbers stay numeric, so they can be totalled or pivoted without cleaning up.',
            },
          ],
        },
        {
          kind: 'paragraph',
          text: 'The report reflects the facility details recorded in the portal. If a Property ID, Area, District, or local facility contact is blank in your report, it is blank against the facility — tell VGS and it will be filled in and carried on every row from then on.',
        },
      ],
    },
  ],
};
